import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, ArrowRight, ArrowLeft, AlertTriangle, Copy, Download } from 'lucide-react';
import { keyed } from '../../lib/listKeys';

const STATUS_BADGE = {
  ok: { label: 'Ready', className: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30' },
  duplicate: { label: 'Duplicate', className: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30' },
  missing_required: { label: 'Invalid data', className: 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/30' },
  conflict: { label: 'Conflicting match', className: 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/30' },
};

export function PreviewStep({ loading, preview, onBack, onNext, onDownloadFailures, kind = 'products', fields = [] }) {
  if (loading) {
    return (
      <div className="py-20 text-center" data-testid="preview-loading">
        <Loader2 className="h-6 w-6 animate-spin mx-auto text-primary mb-3" />
        <p className="text-sm text-muted-foreground">Checking your rows against existing records...</p>
      </div>
    );
  }
  if (!preview) return null;

  const newEntityCount = (preview.new_suppliers?.length || 0) + (preview.new_categories?.length || 0) + (preview.new_manufacturers?.length || 0);

  return (
    <div className="space-y-6" data-testid="preview-step">
      <div className="grid grid-cols-3 gap-4">
        <Card data-testid="preview-total">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold">{preview.total_rows}</p>
            <p className="text-xs text-muted-foreground">Rows in file</p>
          </CardContent>
        </Card>
        <Card data-testid="preview-duplicates">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{preview.duplicate_count}</p>
            <p className="text-xs text-muted-foreground">Duplicate rows</p>
          </CardContent>
        </Card>
        <Card data-testid="preview-missing">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-rose-600 dark:text-rose-400">{preview.missing_required_count}</p>
            <p className="text-xs text-muted-foreground">Rows needing correction</p>
          </CardContent>
        </Card>
      </div>

      {preview.failed_rows?.length > 0 && (
        <Button variant="outline" className="gap-2" onClick={onDownloadFailures} data-testid="preview-download-failed-rows">
          <Download className="h-4 w-4" /> Download {preview.failed_rows.length} failed row{preview.failed_rows.length === 1 ? '' : 's'}
        </Button>
      )}

      {newEntityCount > 0 && (
        <Card className="border-primary/30 bg-primary/5" data-testid="preview-new-entities">
          <CardContent className="p-4">
            <p className="text-sm font-medium mb-2 flex items-center gap-1.5">
              <Copy className="h-4 w-4 text-primary" /> Will be auto-created on import:
            </p>
            <div className="flex flex-wrap gap-2">
              {preview.new_suppliers?.length > 0 && (
                <Badge variant="outline" className="text-xs">{preview.new_suppliers.length} new supplier{preview.new_suppliers.length > 1 ? 's' : ''}</Badge>
              )}
              {preview.new_categories?.length > 0 && (
                <Badge variant="outline" className="text-xs">{preview.new_categories.length} new categor{preview.new_categories.length > 1 ? 'ies' : 'y'}</Badge>
              )}
              {preview.new_manufacturers?.length > 0 && (
                <Badge variant="outline" className="text-xs">{preview.new_manufacturers.length} new manufacturer{preview.new_manufacturers.length > 1 ? 's' : ''}</Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold">Row preview</CardTitle>
          <CardDescription>
            Showing {preview.sample.length} of {preview.total_rows} rows, mapped exactly as they'll be imported.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="max-h-[360px] w-full min-w-0 overflow-y-auto overscroll-contain [overflow-wrap:anywhere]" data-testid="preview-rows-scroll">
            <div className="space-y-1.5">
              {keyed(preview.sample, r => r.row).map(({ item: row, key }) => {
                const badge = STATUS_BADGE[row.status] || STATUS_BADGE.ok;
                return (
                  <div key={key} className="flex items-start gap-2.5 text-sm py-1.5 px-2 rounded hover:bg-muted/40" data-testid={`preview-row-${row.row}`}>
                    <Badge variant="outline" className="text-[10px] shrink-0 mt-0.5">Row {row.row}</Badge>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium min-w-0 max-w-full [overflow-wrap:anywhere]" data-testid={`preview-name-${row.row}`}>{row.name || '(no name)'}</span>
                        <Badge variant="outline" className={`text-[10px] ${badge.className}`}>{badge.label}</Badge>
                        {row.unit_price > 0 && <span className="text-xs text-muted-foreground">${row.unit_price.toFixed(2)}</span>}
                      </div>
                      {row.matched_product && (
                        <p className="text-xs text-amber-700 dark:text-amber-400 mt-1 break-words" data-testid={`preview-match-${row.row}`}>
                          Matched by {row.matched_by?.replaceAll('_', ' ')}: {row.matched_product.name}
                          {row.barcode ? ` · ${row.barcode}` : ''}
                        </p>
                      )}
                      {row.matched_record && <p className="text-xs text-amber-700 dark:text-amber-400 mt-1 break-words" data-testid={`preview-match-${row.row}`}>Matched by {row.matched_by?.replaceAll('_', ' ')}: {row.matched_record.name}</p>}
                      {kind !== 'products' && <details className="mt-2 text-xs" data-testid={`preview-contact-fields-${row.row}`}><summary className="cursor-pointer" data-testid={`preview-contact-toggle-${row.row}`}>Mapped values</summary><dl className="space-y-1 mt-2">{fields.filter(f => f.key !== 'name' && row[f.key] != null && row[f.key] !== '').map(f => <div key={f.key} data-testid={`preview-field-${row.row}-${f.key}`}><dt className="font-medium">{f.label}</dt><dd>{Array.isArray(row[f.key]) ? row[f.key].join('; ') : String(row[f.key])}</dd></div>)}</dl></details>}
                      {row.issues?.length > 0 && (
                        <p className="text-xs text-muted-foreground mt-0.5 break-words" data-testid={`preview-issues-${row.row}`}>{row.issues.join('; ')}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {preview.valid_count === 0 && (
        <p className="text-sm text-rose-600 dark:text-rose-400 flex items-center gap-1.5" data-testid="preview-no-valid-rows">
          <AlertTriangle className="h-4 w-4" /> No rows can be imported yet — check your column mapping.
        </p>
      )}

      <div className="flex justify-between">
        <Button variant="outline" className="gap-1.5" onClick={onBack} data-testid="preview-back-btn">
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <Button className="gap-1.5" onClick={onNext} disabled={preview.valid_count === 0} data-testid="preview-next-btn">
          Next <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
